// Methodology detail drill-ins (design-vs-build Batch 3): Implementatieprogramma / Verbeterpunt.
// These registers use the AggregateCrud kit (which now supports onRowOpen); rows open a detail page
// showing the entity + its governance/remediation child log. Backend: GET
// /{implementation-programs|verbeterpunten}/:id/detail. RLS-scoped.
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft } from "lucide-react";
import { DetailShell, DetailFields, DetailPanel, dv, type DRow } from "./inspector/_detail";

export function ProgramDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/programs/$id" });
  const q = useQuery({ queryKey: ["program-detail", id], queryFn: () => api.getProgramDetail(id) as Promise<{ program: DRow; gates: DRow[] }> });
  const p = (q.data as { program?: DRow } | undefined)?.program;
  const back = <Link to="/programs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="size-4" /> {t("method.detail.backPrograms")}</Link>;
  return (
    <DetailShell back={back} title={dv(p?.name)} q={q as never}>
      {(d) => { const x = d.program as DRow; return (<>
        <DetailFields items={[[t("risk.f.description"), x.description], [t("method.f.lifecycle"), x.lifecycleStatus], [t("method.f.startedAt"), x.startedAt], [t("method.f.expectedAt"), x.expectedCompletionAt]]} />
        <DetailPanel title={t("method.detail.gates")} rows={(d.gates as DRow[]) ?? []} empty={t("method.detail.noGates")}
          renderRow={(r) => (<>
            <span className="min-w-0 truncate"><span className="font-medium">{t("method.f.sprint")} {dv(r.sprintNumber)}</span> <span className="text-muted-foreground">· {dv(r.sprintStageCode)} · {dv(r.rationale)}</span></span>
            <Badge variant="secondary" className="capitalize">{dv(r.decision)}</Badge>
          </>)} />
      </>); }}
    </DetailShell>
  );
}

export function SprintGateDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/sprint-gates/$id" });
  const q = useQuery({ queryKey: ["sprintgate-detail", id], queryFn: () => api.getSprintGateDetail(id) as Promise<DRow> });
  const g = q.data as DRow | undefined;
  const back = <Link to="/sprint-gates" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="size-4" /> {t("method.detail.backGates")}</Link>;
  return (
    <DetailShell back={back} title={g ? `${t("method.f.sprint")} ${dv(g.sprintNumber)} · ${dv(g.sprintStageCode)}` : t("method.detail.gates")} q={q as never}>
      {(r) => (<>
        <DetailFields items={[[t("method.f.decision"), r.decision], [t("method.f.decidedAt"), r.decidedAt ? new Date(String(r.decidedAt)).toLocaleString() : "—"], [t("method.f.rationale"), r.rationale]]} />
        {Boolean(r.programId) && (
          <DetailPanel title={t("method.detail.program")} rows={[{ id: r.programId, name: r.program }]} empty=""
            renderRow={(p) => <Link to="/programs/$id" params={{ id: String(p.id) }} className="font-medium text-primary hover:underline">{dv(p.name)}</Link>} />
        )}
      </>)}
    </DetailShell>
  );
}

export function ChangeTriggerDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/change-triggers/$id" });
  const q = useQuery({ queryKey: ["changetrigger-detail", id], queryFn: () => api.getChangeTriggerDetail(id) as Promise<{ trigger: DRow; events: DRow[] }> });
  const tr = (q.data as { trigger?: DRow } | undefined)?.trigger;
  const back = <Link to="/change-triggers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="size-4" /> {t("method.detail.backTriggers")}</Link>;
  return (
    <DetailShell back={back} title={dv(tr?.title)} q={q as never}>
      {(d) => { const x = d.trigger as DRow; return (<>
        <DetailFields items={[[t("method.f.summary"), x.summary], [t("method.f.sourceKind"), x.sourceKind], [t("method.f.cascade"), x.cascadeScope], [t("method.f.fanOut"), x.fanOutStatus], [t("method.f.detectedAt"), x.detectedAt ? new Date(String(x.detectedAt)).toLocaleString() : "—"]]} />
        <DetailPanel title={t("method.detail.events")} rows={(d.events as DRow[]) ?? []} empty={t("method.detail.noEvents")}
          renderRow={(e) => (<>
            <span className="min-w-0 truncate"><span className="font-medium">{dv(e.eventKind)}</span> <span className="text-muted-foreground">· {dv(e.reason)}</span></span>
            <span className="shrink-0 text-xs text-muted-foreground">{e.occurredAt ? new Date(String(e.occurredAt)).toLocaleDateString() : "—"}</span>
          </>)} />
      </>); }}
    </DetailShell>
  );
}

export function VerbeterpuntDetailRoute() {
  const { t } = useI18n();
  const { id } = useParams({ from: "/verbeterpunten/$id" });
  const q = useQuery({ queryKey: ["verbeterpunt-detail", id], queryFn: () => api.getVerbeterpuntDetail(id) as Promise<{ verbeterpunt: DRow; assessments: DRow[] }> });
  const v = (q.data as { verbeterpunt?: DRow } | undefined)?.verbeterpunt;
  const back = <Link to="/verbeterpunten" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="size-4" /> {t("method.detail.backVerbeter")}</Link>;
  return (
    <DetailShell back={back} title={dv(v?.title)} q={q as never}>
      {(d) => { const x = d.verbeterpunt as DRow; return (<>
        <DetailFields items={[[t("risk.f.description"), x.description], [t("method.f.status"), x.status], [t("method.f.sourceTier"), x.sourceTier]]} />
        <DetailPanel title={t("method.detail.assessments")} rows={(d.assessments as DRow[]) ?? []} empty={t("method.detail.noAssessments")}
          renderRow={(r) => (<>
            <span className="min-w-0 truncate"><span className="font-medium">{dv(r.passKind)}</span> <span className="text-muted-foreground">· {dv(r.cycleRef)} · {dv(r.aiVerdict)}</span></span>
            <span className="shrink-0 text-xs text-muted-foreground">{r.occurredAt ? new Date(String(r.occurredAt)).toLocaleDateString() : "—"}</span>
          </>)} />
      </>); }}
    </DetailShell>
  );
}
